import { getCurrentUser } from '@/hooks/query/useQueryUser';
import { useEnableBlockEditorV1 } from '@/hooks/useEnableBlockEditorV1';
import { updateConsultNote } from '@/pages/api/scribeAPIV2';
import { offlineRecordingBridge } from '@/tauri/offline-recording/recording';
import { logger } from '@/utils/logger/logger';
import {
  applyOfflineSettingsToSessionCache,
  isOfflineLocalSession,
  toOfflineRecordingSettings,
} from '@/utils/offline-recording/localSessionSettings';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { ContentType, GenerationMethod } from '@heidi/schema/ml-scribe';
import {
  DEFAULT_TEMPLATE_AUTO_MODE,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

function isRealTemplateId(templateId: string | null | undefined): boolean {
  return Boolean(templateId && templateId !== DEFAULT_TEMPLATE_AUTO_MODE);
}

/**
 * Ensures a session has a template / generation method selected before note generation.
 *
 * Resolution (lockstep with Rust `resolve_note_defaults`):
 * 1. Session already has a real consult_note.template_id → keep (no-op)
 * 2. User session_settings.default_template_id → keep (no-op; engine / post-recording uses it)
 * 3. Otherwise apply AUTO_MODE as generation_method (not as a TEMPLATE template_id)
 *
 * Cache miss: skip the write — the server may still hold a clinician-chosen
 * template; Rust bound-session enrichment covers that path.
 *
 * All data is read from query cache at call time (not from hook closures)
 * to avoid stale closure issues in async flows.
 */
// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- existing desktop stop helper under src/hooks/desktop; not templates/crud data-layer (only PATCHes consult-note generation_method for Chronicle reconnect-stop)
export function useAutoSetSessionDefaultTemplate() {
  const queryClient = useQueryClient();
  const enableBlockEditorV1 = useEnableBlockEditorV1();

  const ensureTemplateSelected = useCallback(
    async (sessionId: string): Promise<{ wasAutoApplied: boolean }> => {
      // NOTE: Reads from cache only — assumes session is already fetched during
      // the active recording flow.
      const session = reactQueryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId },
      ]);
      const user = getCurrentUser();

      // Cache miss: do not PATCH. A miss after Remote disconnect/reconnect often
      // means the cache is empty while the server still has the clinician's
      // template — writing here would clobber it.
      if (!session) {
        logger.debug(
          '[useAutoSetSessionDefaultTemplate] Session cache miss — skipping AUTO_MODE write',
          { sessionId }
        );
        return { wasAutoApplied: false };
      }

      const sessionTemplateId = session.consult_note?.template_id;
      const hasSessionTemplate = isRealTemplateId(sessionTemplateId);
      const hasUserDefault = Boolean(user?.session_settings?.default_template_id);
      const alreadyAutoMode =
        session.consult_note?.generation_method === GenerationMethod.AUTO_MODE &&
        !isRealTemplateId(sessionTemplateId);

      logger.debug('[useAutoSetSessionDefaultTemplate] Checking template state', {
        sessionId,
        hasSessionTemplate,
        sessionTemplateId: sessionTemplateId ?? null,
        hasUserDefault,
        userDefaultTemplateId: user?.session_settings?.default_template_id ?? null,
        alreadyAutoMode,
      });

      if (hasSessionTemplate || hasUserDefault || alreadyAutoMode) {
        return { wasAutoApplied: false };
      }

      logger.info(
        '[useAutoSetSessionDefaultTemplate] No template configured, applying AUTO_MODE generation method',
        { sessionId }
      );

      // Optimistic cache: generation_method path — never persist AUTO_MODE as
      // template_id (that would force TEMPLATE generation against a non-UUID).
      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            consult_note: {
              ...oldData.consult_note,
              template_id: undefined,
              generation_method: GenerationMethod.AUTO_MODE,
            },
          };
        }
      );

      if (isOfflineLocalSession(session)) {
        const offlineSettings = toOfflineRecordingSettings({
          generationMethod: GenerationMethod.AUTO_MODE,
        });
        await offlineRecordingBridge.updateSettings(sessionId, offlineSettings);
        queryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId }],
          (oldData) => applyOfflineSettingsToSessionCache(oldData, offlineSettings)
        );
      } else {
        await updateConsultNote({
          request: {
            content_type: enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN,
            generation_method: GenerationMethod.AUTO_MODE,
          },
          sessionId,
        });
      }

      return { wasAutoApplied: true };
    },
    [queryClient, enableBlockEditorV1]
  );

  return { ensureTemplateSelected };
}
